import { Component, Input, OnInit, input, output, inject, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HouseholdStudent } from '@k12/models';
import { CfiRecordViewComponent, FieldOption, RecordSection } from '@k12/app-components';
import { STUDENT_INFORMATION_SECTIONS, STUDENT_INFORMATION_OPTIONS } from './student-information.config';
import { getEndorsementDisplay, isEndorsed } from './student-endorsement.util';
import { resolveEnrollmentStatusId } from './student-enrollment-status.util';
import { FormatDatePipe } from '@k12/pipes';


@Component({
  selector: 'student-information',
  standalone: true,
  imports: [CfiRecordViewComponent],
  templateUrl: './student-information.html',
  styleUrl: './student-information.scss'
})
export class StudentInformationComponent implements OnInit, OnChanges {
  @Input() householdStudent: HouseholdStudent | null = null;

  public readonly showEditButton = input<boolean>(false);
  public readonly showInitiateSchoolTransferButton = input<boolean>(false);
  public readonly isWithdrawalPending = input<boolean>(false);
  public readonly showChangeGuardianButton = input<boolean>(false);
  public readonly canEditStudentInfo = input<boolean>(false);
  public readonly canEditSchoolInfo = input<boolean>(false);

  public readonly initiateSchoolTransfer = output<void>();
  public readonly initiateChangeGuardian = output<void>();
  public readonly editRequested = output<void>();

  public readonly sections: RecordSection[] = STUDENT_INFORMATION_SECTIONS;
  public readonly options: Record<string, FieldOption[]> = {
    ...STUDENT_INFORMATION_OPTIONS,
    schools: [],
    schoolTypes: [],
    gradeLevels: []
  };

  private readonly fb = inject(FormBuilder);

  public readonly form: FormGroup = this.fb.group({
    ...STUDENT_INFORMATION_SECTIONS
      .flatMap((s) => s.fields)
      .reduce<Record<string, [null, []]>>((acc, field) => {
        acc[field.key] = [null, []];
        return acc;
      }, {}),

    endorsedByParentFallDisplay: [null],
    endorsedByParentSpringDisplay: [null]
  });

  public studentName = '';

  public ngOnInit(): void {
    this.initFromStudent(this.householdStudent);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['householdStudent']) {
      this.initFromStudent(this.householdStudent);
    }
  }

  private initFromStudent(student: HouseholdStudent | null): void {
    if (!student) return;
    this.studentName = this.getStudentName(student);
    this.options['schools'] = [{ label: student.schoolName ?? '—', value: student.schoolId ?? '—' }];
    this.options['schoolTypes'] = [{ label: student.schoolType ?? '—', value: student.schoolTypeId ?? -1 }];
    this.options['gradeLevels'] = [{ label: student.grade ?? '—', value: student.gradeId ?? -1 }];
    this.form.patchValue({
      ...student,
      attendanceTypeId: resolveEnrollmentStatusId(student),
      dob: FormatDatePipe.toDate(student.dob),
      ssn: student.lastFourSSN ? `XXX-XX-${student.lastFourSSN}` : '—',
      endorsedBySchoolFall: isEndorsed(student.endorsedBySchoolFall),
      endorsedBySchoolSpring: isEndorsed(student.endorsedBySchoolSpring),
      endorsedByParentFallDisplay: getEndorsementDisplay(student.endorsedByParentFall),
      endorsedByParentSpringDisplay: getEndorsementDisplay(student.endorsedByParentSpring)
    });
  }

  public onEditClick(): void {
    if (!this.householdStudent) return;
    this.editRequested.emit();
  }

  public getHeaderTitle(): string {
    const student = this.householdStudent;
    const fullName = `${student?.prefix ? `${student.prefix}. ` : ''}${this.studentName}`.trim();
    return fullName || 'Student Information';
  }

  public getHeaderPid(): string {
    return this.householdStudent?.pidNumber || '—';
  }

  public onInitiateSchoolTransferClick(): void {
    this.initiateSchoolTransfer.emit();
  }

  public isInitiateSchoolTransferDisabled(): boolean {
    return this.isWithdrawalPending();
  }

  public getInitiateSchoolTransferButtonLabel(): string {
    return this.isWithdrawalPending() ? 'Withdrawal Pending' : 'Initiate Withdrawal / Transfer';
  }

  public onChangeGuardianClick(): void {
    this.initiateChangeGuardian.emit();
  }

  private getStudentName(student: HouseholdStudent): string {
    return [student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ');
  }
}
